<template>
    <v-row justify="center">
        <v-dialog v-model="dialog" persistent max-width="1100px" scrollable>
            <v-card v-if="!createdGrowthTask">
                <v-card-title class="primary white--text">
                    Create Growth Task
                    <v-spacer />
                    <v-btn icon dark @click="closeMainDialog">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-card-title>

                <div v-if="isLoadingPlannedTask" class="my-5 mx-3">
                    <v-progress-linear
                        indeterminate
                        rounded
                    ></v-progress-linear>
                </div>

                <v-card-text v-else class="px-8 py-4">
                    <v-form>
                        <!-- Plant -->
                        <v-card-subtitle class="px-0">
                            <v-icon>spa</v-icon>
                            {{
                                selectedPlant && selectedPlant.plant
                                    ? selectedPlant.plant.name
                                    : ""
                            }}
                        </v-card-subtitle>

                        <v-row class="px-3">
                            <v-chip class="mr-1 mb-1" small>
                                <v-avatar left>
                                    <v-icon>straighten</v-icon>
                                </v-avatar>
                                {{
                                    selectedPlant && selectedPlant.potSize
                                        ? selectedPlant.potSize.potSizeCm
                                        : "-"
                                }}cm
                            </v-chip>
                            <v-chip class="mr-1 mb-1" small>
                                <v-avatar left>
                                    <v-icon>color_lens</v-icon>
                                </v-avatar>
                                {{
                                    selectedPlant && selectedPlant.color
                                        ? selectedPlant.color.color
                                        : "-"
                                }}
                            </v-chip>
                            <v-chip class="mr-1 mb-1" small>
                                <v-avatar left>
                                    <v-icon>blur_on</v-icon>
                                </v-avatar>
                                {{
                                    selectedPlant && selectedPlant.environment
                                        ? selectedPlant.environment.environment
                                        : "-"
                                }}
                            </v-chip>
                            <v-chip class="mr-1 mb-1" small>
                                <v-avatar left>
                                    <v-icon>turned_in_not</v-icon>
                                </v-avatar>
                                {{
                                    selectedPlant && selectedPlant.tag
                                        ? selectedPlant.tag.tag
                                        : "-"
                                }}
                            </v-chip></v-row
                        >

                        <!-- Quantity and Supplier -->
                        <v-card-subtitle class="px-0 mt-3">
                            <v-icon>local_atm</v-icon>
                            Quantity & Supplier
                        </v-card-subtitle>

                        <v-row class="align-center">
                            <v-col cols="12" md="3">
                                <v-text-field
                                    disabled
                                    v-model="quantity"
                                    label="Quantity*"
                                    :rules="[rules.required]"
                                    min="1"
                                    type="number"
                                    outlined
                                    class="subtitle-2 mb-n5"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="9">
                                <v-text-field
                                    v-model="note"
                                    label="Note"
                                    outlined
                                    class="subtitle-2 mb-n5"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="supplierOrderNo"
                                    label="Supplier Order No"
                                    outlined
                                    class="subtitle-2 mb-n5"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="unitPurchasePrice"
                                    prepend-inner-icon="euro_symbol"
                                    label="Purchase Price (unit)"
                                    outlined
                                    class="subtitle-2 mb-n5"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="supplierNodeId"
                                    :items="suppliers.edges"
                                    item-text="node.name"
                                    item-value="node.id"
                                    :loading="$apollo.queries.suppliers.loading"
                                    label="Supplier"
                                    class="subtitle-2 mb-n5"
                                    outlined
                                    clearable
                                ></v-autocomplete>
                            </v-col>
                        </v-row>

                        <!-- Phases -->
                        <v-card-subtitle class="px-0 mt-3">
                            <v-icon>assignment</v-icon>
                            Phases
                        </v-card-subtitle>

                        <v-row
                            v-for="(phase, index) in plannedTask.phases"
                            v-bind:key="index"
                        >
                            <v-col cols="3" md="1">
                                <v-text-field
                                    :value="
                                        phase.isSalesPhase
                                            ? 'Sales'
                                            : phase.phaseNo
                                    "
                                    label="Phase"
                                    outlined
                                    disabled
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    disabled
                                    :value="formateDateToWeek(phase.startDate)"
                                    label="Start Week*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="4" md="2">
                                <v-text-field
                                    v-model="phase.numDays"
                                    :rules="[rules.required]"
                                    type="number"
                                    min="1"
                                    label="#Days*"
                                    outlined
                                    disabled
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="5" md="2">
                                <v-text-field
                                    v-model="phase.numPlantsPerM2"
                                    :rules="[rules.required]"
                                    type="number"
                                    min="1"
                                    label="#Plants / m2*"
                                    outlined
                                    disabled
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="5" md="2">
                                <v-text-field
                                    :value="
                                        calculateSpaceM2(
                                            phase.numPlantsPerM2,
                                            quantity
                                        )
                                    "
                                    type="number"
                                    label="Space m2*"
                                    outlined
                                    disabled
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-autocomplete
                                    v-model="
                                        phase.greenhouseDepartmentSectionNodeId
                                    "
                                    :items="greenhouseDepartmentSections"
                                    item-value="id"
                                    item-text="name"
                                    :loading="
                                        $apollo.queries.greenhouses.loading
                                    "
                                    :rules="[rules.required]"
                                    label="Greenhouse*"
                                    class="subtitle-2"
                                    outlined
                                >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                        >{{ item.fullName }}</template
                                    >
                                    <template
                                        slot="item"
                                        slot-scope="{ item }"
                                        >{{ item.fullName }}</template
                                    >
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions v-if="selectedPlant !== null" class="pr-5 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn outlined rounded @click="closeMainDialog()"
                        >Cancel</v-btn
                    >
                    <v-btn
                        outlined
                        rounded
                        color="primary"
                        :loading="isSaving"
                        :disabled="plannedTask.startDate === null"
                        @click="createGrowthTask(plannedTask.id)"
                        >Create</v-btn
                    >
                </v-card-actions>
            </v-card>

            <v-card v-else>
                <v-card-title class="primary white--text">
                    Create Growth Task
                    <v-spacer />
                    <v-btn icon dark @click="closeMainDialog">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-card-title>

                <GrowthTaskQrCode :task="createdGrowthTask"></GrowthTaskQrCode>

                <v-card-actions class="px-8 pb-5">
                    <v-spacer></v-spacer>
                    <v-btn
                        rounded
                        outlined
                        class="primary--text"
                        min-width="120px"
                        v-print="printObject"
                        >Print</v-btn
                    >
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import moment from "moment";
import GrowthTaskQrCode from "@/components/growth/GrowthTaskQrCode.vue";

export default {
    name: "create-growth-task-from-planned-task-dialog",
    props: ["dialog"],
    components: {
        GrowthTaskQrCode
    },

    apollo: {
        suppliers: {
            query: gql`
                query suppliers {
                    suppliers {
                        edges {
                            node {
                                id
                                name
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.suppliers,
            skip() {
                return !this.dialog;
            }
            // pollInterval: 300 // ms
        },
        greenhouses: {
            query: gql`
                query greenhouses {
                    greenhouses {
                        edges {
                            node {
                                id
                                name
                                greenhouseDepartmentSet(
                                    isPlaceholder: false
                                    orderBy: "name"
                                ) {
                                    edges {
                                        node {
                                            id
                                            name
                                            greenhouseDepartmentSectionSet(
                                                isPlaceholder: false
                                                orderBy: "name"
                                            ) {
                                                edges {
                                                    node {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.greenhouses,
            skip() {
                return !this.dialog;
            }
            // pollInterval: 300 // ms
        }
    },

    data: function() {
        return {
            // isLoadingPlants: false,
            // isLoadingSuppliers: false,
            // isLoadingGreenhouseDepartmentSections: false,
            isLoadingPlannedTask: false,
            configPlantDialog: false,
            isSaving: false,
            updatedPlannedTaskNodeId: null,
            isImportPhasesFromTemplate: true,

            createdGrowthTask: null,

            numSaleDaysChoices: [1, 2, 3, 4, 5],
            numPhasesChoices: [1, 2, 3, 4, 5],

            defaultItemsPerPage: 10,
            dataTableOptions: {},
            page: 1,
            tenantPlants: {},

            step: 1,
            search: null,
            configingPlant: {},
            selectedPlant: null,
            supplierNodeId: null,
            supplierOrderNo: null,
            unitPurchasePrice: null,
            note: null,
            selectedTemplateNodeId: null,
            quantity: null,
            // currentGreenhouseDepartmentSectionNodeId: null,
            currentPhaseNo: 1,
            // currentSpaceM2: null,

            suppliers: {},
            greenhouses: {},

            plannedTask: {
                numPhases: 1,
                startDate: null,
                phases: [
                    {
                        isSalesPhase: false,
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        startDate: null,
                        endDate: null
                    }
                ],
                sales: {
                    isSalesPhase: true,
                    numDays: 3
                }
            },

            defaultPlannedTask: {
                numDays: 3,
                numPhases: 1,
                startDate: null,
                phases: [
                    {
                        isSalesPhase: false,
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        startDate: null,
                        endDate: null
                    }
                ],
                sales: {
                    isSalesPhase: true,
                    phaseNo: 1,
                    numDays: 3
                }
            },

            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            },

            printObject: {
                id: "printMe",
                preview: false,
                // popTitle: 'good print',
                // extraCss: "https://cdn.bootcdn.net/ajax/libs/animate.css/4.1.1/animate.compat.css, https://cdn.bootcdn.net/ajax/libs/hover.css/2.3.1/css/hover-min.css",
                // extraHead: '<meta http-equiv="Content-Language"content="zh-cn"/>',
                // previewBeforeOpenCallback(vue) {
                //     vue.printLoading = true;
                //     console.log("Loading preview");
                // },
                // previewOpenCallback(vue) {
                //     console.log("Preview loaded");
                //     vue.printLoading = false;
                // },
                // beforeOpenCallback(vue) {
                //     console.log("Before open");
                // },
                // openCallback() {
                //     console.log("Opend printer window");
                // },
                closeCallback(vue) {
                    // console.log("Closed printer window");
                    vue.closeMainDialog();
                }
            }
        };
    },
    computed: {
        greenhouseDepartmentSections() {
            var _sections = [];

            if (this.greenhouses.edges) {
                for (let greenhouse of this.greenhouses.edges) {
                    if (greenhouse.node.greenhouseDepartmentSet) {
                        for (let department of greenhouse.node
                            .greenhouseDepartmentSet.edges) {
                            if (
                                department.node.greenhouseDepartmentSectionSet
                            ) {
                                for (let section of department.node
                                    .greenhouseDepartmentSectionSet.edges) {
                                    _sections.push({
                                        id: section.node.id,
                                        name: section.node.name,
                                        fullName:
                                            greenhouse.node.name +
                                            "-" +
                                            department.node.name +
                                            "-" +
                                            section.node.name
                                    });
                                }
                            }
                        }
                    }
                }
            }

            return _sections;
        }
    },
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        initializePlannedTask(plannedTaskNodeId) {
            // console.log(plannedTaskNodeId);

            this.isLoadingPlannedTask = true;

            this.step = 4;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query plannedTask($id: ID!) {
                            plannedTask(id: $id) {
                                id
                                quantity
                                numPhases
                                supplierOrderNo
                                unitPurchasePrice
                                totalGrowthDays
                                maxSalesDays
                                growthStartDate
                                growthEndDate
                                note
                                supplier {
                                    id
                                    name
                                }
                                plant {
                                    id
                                    name
                                }
                                plantGrowthTemplate {
                                    id
                                    name
                                }
                                potSize {
                                    id
                                    potSizeCm
                                }
                                color {
                                    id
                                    color
                                }
                                environment {
                                    id
                                    environment
                                }
                                tag {
                                    id
                                    tag
                                }
                                plannedTaskPhaseSet {
                                    edges {
                                        node {
                                            id
                                            phaseNo
                                            isSalesPhase
                                            numDays
                                            startDate
                                            endDate
                                            numPlantsPerM2
                                            spaceM2
                                            greenhouseDepartmentSection {
                                                id
                                                name
                                                greenhouseDepartment {
                                                    id
                                                    name
                                                    greenhouse {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        id: plannedTaskNodeId
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result
                    // console.log(response.data);

                    this.step = 4;

                    this.updatedPlannedTaskNodeId =
                        response.data.plannedTask.id;

                    // initialize step 1: selected plant
                    this.selectedPlant = {
                        ...this.selectedPlant,
                        plant: response.data.plannedTask.plant,
                        potSize: response.data.plannedTask.potSize,
                        color: response.data.plannedTask.color,
                        environment: response.data.plannedTask.environment,
                        tag: response.data.plannedTask.tag
                    };

                    // initialize step 2: quantity and supplier
                    this.quantity = response.data.plannedTask.quantity;
                    this.note = response.data.plannedTask.note;
                    this.supplierOrderNo =
                        response.data.plannedTask.supplierOrderNo;
                    this.unitPurchasePrice =
                        response.data.plannedTask.unitPurchasePrice;
                    this.supplierNodeId = response.data.plannedTask.supplier
                        ? response.data.plannedTask.supplier.id
                        : null;

                    // initialize step 3: phases
                    this.selectedTemplateNodeId = response.data.plannedTask
                        .plantGrowthTemplate
                        ? response.data.plannedTask.plantGrowthTemplate.id
                        : null;

                    this.isImportPhasesFromTemplate = this
                        .selectedTemplateNodeId
                        ? true
                        : false;

                    this.plannedTask = {
                        ...this.plannedTasks,
                        id: response.data.plannedTask.id,
                        numPhases: response.data.plannedTask.numPhases,
                        startDate: response.data.plannedTask.growthStartDate,
                        phases: response.data.plannedTask.plannedTaskPhaseSet.edges
                            // .filter((item) => {
                            //     return item.node.isSalesPhase === false;
                            // })
                            .map(item => {
                                return {
                                    isSalesPhase: item.node.isSalesPhase,
                                    phaseNo: item.node.phaseNo,
                                    // greenhouseDepartmentSectionNodeId:
                                    //     item.node.greenhouseDepartmentSection
                                    //         .id,
                                    // greenhouseDepartmentSection:
                                    //     item.node.greenhouseDepartmentSection,
                                    greenhouseDepartmentSectionNodeId: item.node
                                        .greenhouseDepartmentSection
                                        ? item.node.greenhouseDepartmentSection
                                              .id
                                        : null,
                                    numDays: item.node.numDays,
                                    numPlantsPerM2: item.node.numPlantsPerM2,
                                    startDate: item.node.startDate,
                                    endDate: item.node.endDate
                                };
                            }),
                        sales: {
                            isSalesPhase: true,
                            numDays: response.data.plannedTask.maxSalesDays
                        }

                        // numPhases: 1,
                        // startDate: null,
                        // phases: [
                        //     {
                        //         isSalesPhase: false,
                        //         phaseNo: 1,
                        //         numDays: null,
                        //         numPlantsPerM2: null,
                        //         startDate: null,
                        //         endDate: null
                        //     }
                        // ],
                        // sales: {
                        //     isSalesPhase: true,
                        //     numDays: 3
                        // }
                    };

                    // console.log(this.plannedTask);
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    this.isLoadingPlannedTask = false;
                });
        },

        resetFormStep1() {
            this.step = 1;
            this.search = "";
            this.configingPlant = null;
            this.selectedPlant = null;
            this.supplierNodeId = null;
            this.supplierOrderNo = null;
            this.note = null;
            this.selectedTemplateNodeId = null;
            this.quantity = null;
            this.unitPurchasePrice = null;
            // this.currentGreenhouseDepartmentSectionNodeId = null;
            this.currentPhaseNo = 1;
            this.isImportPhasesFromTemplate = true;
            // this.currentSpaceM2 = null;
            this.createdGrowthTask = null;

            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
        },

        resetFormStepPhases() {
            this.isSaving = false;
            this.selectedTemplateNodeId = null;
            this.plannedTask = JSON.parse(
                JSON.stringify(this.defaultPlannedTask)
            );

            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
        },

        calculateSpaceM2(numPlantsPerM2, quantity) {
            return numPlantsPerM2 > 0
                ? Math.ceil(quantity / numPlantsPerM2)
                : 0;
        },

        formateDateToWeek(date) {
            if (!date) {
                return null;
            }

            return moment(date).format("YYYY-WW");
        },

        createGrowthTask() {
            this.isSaving = true;

            var _phases = this.plannedTask.phases.map(item => {
                return {
                    phaseNo: item.phaseNo,
                    greenhouseDepartmentSectionNodeId:
                        item.greenhouseDepartmentSectionNodeId
                };
            });

            // console.log(_phases);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation createGrowthTaskFromPlannedTask(
                            $input: CreateGrowthTaskFromPlannedTaskInput!
                        ) {
                            createGrowthTaskFromPlannedTask(input: $input) {
                                growthTask {
                                    id
                                    rawId
                                    growthStartDate
                                    totalInventory
                                    supplier {
                                        id
                                        name
                                    }
                                    plannedTask {
                                        id
                                        isDone
                                    }
                                    plant {
                                        id
                                        name
                                        plantSpecies {
                                            id
                                            name
                                        }
                                    }
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    color {
                                        id
                                        color
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            plannedTaskNodeId: this.plannedTask.id,
                            supplierNodeId: this.supplierNodeId,
                            supplierOrderNo: this.supplierOrderNo,
                            unitPurchasePrice: this.unitPurchasePrice,
                            note: this.note,
                            phases: _phases
                        }
                    }
                })
                .then(response => {
                    // // Result
                    // console.log(data);
                    this.$emit("item-created", false);

                    this.createdGrowthTask =
                        response.data.createGrowthTaskFromPlannedTask.growthTask;

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Growth task is created!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // Error
                    console.error(error);

                    let _error = error.graphQLErrors
                        .map(error => error.message)
                        .join(", ");

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: _error
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isSaving = false;

                    // setTimeout(() => {
                    //     //your code to be executed after 1 second
                    //     this.isLoadingPlannedTaskPhases = false;
                    // }, 1000);
                });
        },

        closeMainDialog() {
            this.step = 1;
            this.resetFormStep1();
            this.resetFormStepPhases();
            this.updatedPlannedTaskNodeId = null;
            this.$emit("update:dialog", false);

            if (this.$refs.formStep1) {
                this.resetFormStep1();
                this.refreshCurrentPlantsPage();
            }
            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
            if (this.$refs.formStep4) {
                this.$refs.formStep4.resetValidation();
            }
        },

        goToPlantDetail(plantNodeId) {
            // this.$router.push({
            //     name: "plant-detail",
            //     params: { id: plant.id }
            // });

            let routeData = this.$router.resolve({
                name: "plant-detail",
                params: { id: plantNodeId }
            });
            window.open(routeData.href, "_blank");
        }
    }
};
</script>


<style scoped>
.box-border {
    border-style: solid;
    padding: 30px;
}
</style>

